import type { ClipboardService } from './types';

/**
 * The system clipboard, with its two halves detected separately (ADR-0015).
 *
 * WHY TWO FLAGS AND NOT ONE. `navigator.clipboard` is not one capability. Firefox exposes
 * `writeText` to page script and does NOT expose `readText` at all — the Async Clipboard
 * read path is gated behind a user gesture on a browser-chrome paste, which a page cannot
 * trigger. Safari gates the whole object behind a secure context. So a single
 * `isSupported` would either hide Copy on Firefox (wrong) or render Paste there and fail on
 * click (worse: the user sees a generic error and reads it as our bug — exactly what the
 * legacy build did, see the recon note on `messages.errorPaste`).
 *
 * The toolbar therefore asks `canRead` before it renders Paste, and `canWrite` before it
 * renders Copy. Those are fields, read once at construction, never a throw.
 *
 * WHY DENIAL IS NOT A LEAKED EXCEPTION. A denied permission, a document that is not focused
 * and a browser that never had the API are three different words to say to the user, and
 * they must not arrive as a browser-authored `DOMException` whose `message` differs per
 * engine and per locale. Every failure leaves here as a `ClipboardError` with a `reason`
 * this application chose, and its message NEVER contains any of the user's text.
 */

export type ClipboardFailureReason =
  /** The API half we needed does not exist here (Firefox read, or a non-secure context). */
  | 'unsupported'
  /** The user (or a permissions policy) said no. A normal outcome, not a crash. */
  | 'denied'
  /** It existed, it was allowed, and it still did not work. */
  | 'failed';

/**
 * The only rejection either method of {@link ClipboardService} produces.
 *
 * Carries no user data: the clipboard's content is precisely the thing we promise never to
 * ship anywhere (ADR-0001), and an error message is the easiest place for it to leak.
 */
export class ClipboardError extends Error {
  readonly reason: ClipboardFailureReason;
  readonly operation: 'read' | 'write';

  constructor(operation: 'read' | 'write', reason: ClipboardFailureReason) {
    super(`clipboard ${operation} ${reason}`);
    this.name = 'ClipboardError';
    this.operation = operation;
    this.reason = reason;
  }
}

export function isClipboardError(value: unknown): value is ClipboardError {
  return value instanceof ClipboardError;
}

/**
 * The part of `navigator.clipboard` this application uses.
 *
 * Text only. `read()`/`write()` carry `ClipboardItem`s (images, HTML), which a JSON editor
 * has no use for, and which cost a second permission on Chromium.
 */
interface ClipboardTextApi {
  readText?: () => Promise<string>;
  writeText?: (text: string) => Promise<void>;
}

export interface ClipboardDeps {
  /**
   * Injected in tests. Omitted means "use `navigator.clipboard`, read defensively";
   * `null` means "there is no clipboard here" — the same two-word convention
   * `storage.ts` and `theme.ts` use for their own hosts.
   */
  readonly api?: ClipboardTextApi | null;
  /** Defaults to `globalThis.isSecureContext`. */
  readonly secureContext?: boolean;
}

/** `navigator.clipboard` if it is there, `null` if anything about reaching it throws. */
function ambientApi(): ClipboardTextApi | null {
  try {
    const nav: { clipboard?: ClipboardTextApi } | undefined =
      typeof navigator === 'undefined' ? undefined : navigator;
    return nav?.clipboard ?? null;
  } catch {
    return null;
  }
}

function ambientSecureContext(): boolean {
  try {
    // `isSecureContext` is universally present; `!== false` keeps a host that omits it
    // (jsdom in an odd configuration, a test shim) from disabling the whole feature.
    return (globalThis as { isSecureContext?: boolean }).isSecureContext !== false;
  } catch {
    return true;
  }
}

/**
 * Map whatever the platform threw onto one of our three words.
 *
 * `NotAllowedError` is what every engine raises for a refused permission, and also for a
 * write attempted without a user gesture — both are "the user did not let us", which is the
 * sentence the toast wants. `SecurityError` is the non-secure-context refusal.
 */
function classify(cause: unknown): ClipboardFailureReason {
  const name = (cause as { name?: unknown } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'NotFoundError') {
    return 'denied';
  }
  return 'failed';
}

export function createClipboardService(deps: ClipboardDeps = {}): ClipboardService {
  const api = deps.api !== undefined ? deps.api : ambientApi();
  const secure = deps.secureContext ?? ambientSecureContext();

  const canRead = secure && typeof api?.readText === 'function';
  const canWrite = secure && typeof api?.writeText === 'function';

  return {
    canRead,
    canWrite,

    async read(): Promise<string> {
      const readText = api?.readText;
      if (!canRead || typeof readText !== 'function') {
        throw new ClipboardError('read', 'unsupported');
      }
      try {
        // `.call(api)` and not a bare `readText()`: the real method needs its receiver.
        return await readText.call(api);
      } catch (cause) {
        throw new ClipboardError('read', classify(cause));
      }
    },

    async write(text: string): Promise<void> {
      const writeText = api?.writeText;
      if (!canWrite || typeof writeText !== 'function') {
        throw new ClipboardError('write', 'unsupported');
      }
      try {
        await writeText.call(api, text);
      } catch (cause) {
        throw new ClipboardError('write', classify(cause));
      }
    },
  };
}

/**
 * A clipboard that is not there. Both flags `false`, both methods reject `unsupported`.
 *
 * This is what `createNullServices()` installs: a command test asserting "Paste is hidden
 * when the browser has no read half" needs the negative case as the default, and a test
 * that wants a working one passes `createFakeClipboard()` from `test/helpers/nullServices`.
 */
export function createNullClipboardService(): ClipboardService {
  return {
    canRead: false,
    canWrite: false,
    read(): Promise<string> {
      return Promise.reject(new ClipboardError('read', 'unsupported'));
    },
    write(): Promise<void> {
      return Promise.reject(new ClipboardError('write', 'unsupported'));
    },
  };
}
